/* global globalThis */
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { FaEye } from 'react-icons/fa';
import '../../../styles/dashboard/OrgUser/Wallet.css';
import OrgUserService from '../../../services/orgUser.service';

const LCS_ORIGIN_STATE = String(process.env.REACT_APP_LCS_GST_ORIGIN_STATE).toLowerCase();
const MIN_TOPUP_AMOUNT = 2000;
const QUICK_TOPUP_AMOUNTS = [2000, 5000, 10000, 20000];

const extractPerformedBy = (transaction) => {
  if (transaction?.performedByName) return transaction.performedByName;
  const description = transaction?.description;
  if (!description) return '-';
  const match = /\[by (.+?)\]$/.exec(String(description));
  return match ? match[1] : '-';
};

const stripPerformedBy = (description) => {
  if (!description) return '-';
  return String(description).replace(/\s*\[by .+?\]$/, '').trim() || '-';
};

const toTwo = (value) => Number.parseFloat(Number(value || 0).toFixed(2));

const ensureRazorpayScript = () => new Promise((resolve) => {
  if (globalThis.Razorpay) {
    resolve(true);
    return;
  }

  const script = document.createElement('script');
  script.src = 'https://checkout.razorpay.com/v1/checkout.js';
  script.async = true;
  script.onload = () => resolve(true);
  script.onerror = () => resolve(false);
  document.body.appendChild(script);
});

export default function WalletOrgUser() {
  const formatAmount = (value) => new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

  const [transactionFilter, setTransactionFilter] = useState('all');
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [topupModalOpen, setTopupModalOpen] = useState(false);
  const [topupAmount, setTopupAmount] = useState('');
  const [topupPreview, setTopupPreview] = useState({
    taxableAmount: 0,
    platformFee: 0,
    gstAmount: 0,
    cgstAmount: 0,
    sgstAmount: 0,
    igstAmount: 0,
    totalAmount: 0,
    isInterState: false,
  });
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, pages: 0 });
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [downloading, setDownloading] = useState(false);

  const loadWallet = async () => {
    try {
      const res = await OrgUserService.getWalletInfo();
      setWallet(res.data || res);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to load wallet info', err);
    }
  };

  const loadTransactions = async (page = 1, type = '', start = '', end = '') => {
    setLoading(true);
    try {
      const res = await OrgUserService.getWalletTransactions(page, pagination.limit, type, start, end);
      const list = res.data || res?.data?.data || res?.data || [];
      setTransactions(list);
      if (res.pagination) {
        setPagination(res.pagination);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to load wallet transactions', err);
    }
    setLoading(false);
  };

  // Initial fetch on mount.
  useEffect(() => {
    loadWallet();
    loadTransactions(pagination.page, transactionFilter === 'all' ? '' : transactionFilter, startDate, endDate);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFilterChange = (type) => {
    setTransactionFilter(type);
    setPagination({ ...pagination, page: 1 });
    loadTransactions(1, type === 'all' ? '' : type, startDate, endDate);
  };

  const handleDateFilter = () => {
    setPagination({ ...pagination, page: 1 });
    loadTransactions(1, transactionFilter === 'all' ? '' : transactionFilter, startDate, endDate);
  };

  const clearDateFilter = () => {
    setStartDate('');
    setEndDate('');
    setPagination({ ...pagination, page: 1 });
    loadTransactions(1, transactionFilter === 'all' ? '' : transactionFilter, '', '');
  };

  const handlePageChange = (newPage) => {
    setPagination({ ...pagination, page: newPage });
    loadTransactions(newPage, transactionFilter === 'all' ? '' : transactionFilter, startDate, endDate);
  };

  const handleDownloadStatement = async () => {
    setDownloading(true);
    try {
      const blob = await OrgUserService.downloadWalletStatement(transactionFilter === 'all' ? '' : transactionFilter, startDate, endDate);
      const url = globalThis.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `wallet-statement-${Date.now()}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      globalThis.URL.revokeObjectURL(url);
      showToast('Statement downloaded successfully');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to download statement', err);
      showToast('Failed to download statement', 'error');
    }
    setDownloading(false);
  };

  const openTopupModal = () => {
    setTopupAmount(String(MIN_TOPUP_AMOUNT));
    recalculateTopupPreview(MIN_TOPUP_AMOUNT);
    setTopupModalOpen(true);
  };

  const closeTopupModal = () => {
    recalculateTopupPreview('');
    setTopupModalOpen(false);
  };

  const applyTopupAmount = (amount) => {
    setTopupAmount(String(amount));
    recalculateTopupPreview(amount);
  };

  const recalculateTopupPreview = (amountValue) => {
    const baseAmount = Number.parseFloat(amountValue || 0);
    if (!baseAmount || baseAmount <= 0) {
      setTopupPreview({
        taxableAmount: 0,
        platformFee: 0,
        gstAmount: 0,
        cgstAmount: 0,
        sgstAmount: 0,
        igstAmount: 0,
        totalAmount: 0,
        isInterState: false,
      });
      return;
    }

    const orgState = String(wallet?.organization?.state || '').toLowerCase();
    const isInterState = orgState && orgState !== LCS_ORIGIN_STATE;
    const platformFee = toTwo(baseAmount * 0.02);
    const gstAmount = toTwo(baseAmount * 0.18);

    setTopupPreview({
      taxableAmount: toTwo(baseAmount),
      platformFee,
      gstAmount,
      cgstAmount: isInterState ? 0 : toTwo(gstAmount / 2),
      sgstAmount: isInterState ? 0 : toTwo(gstAmount / 2),
      igstAmount: isInterState ? gstAmount : 0,
      totalAmount: toTwo(baseAmount + platformFee + gstAmount),
      isInterState,
    });
  };

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
  };

  const handleInitiateTopup = async (e) => {
    e.preventDefault();
    const amountNum = Number.parseFloat(topupAmount || 0);
    if (Number.isNaN(amountNum) || amountNum < MIN_TOPUP_AMOUNT) {
      showToast(`Minimum wallet recharge amount is ₹${MIN_TOPUP_AMOUNT}`, 'error');
      return;
    }

    try {
      const scriptLoaded = await ensureRazorpayScript();
      if (!scriptLoaded || !globalThis.Razorpay) {
        showToast('Failed to load Razorpay checkout script', 'error');
        return;
      }

      const res = await OrgUserService.initiateWalletTopup(amountNum);
      const payload = res.data || res;
      if (!payload.orderId || !payload.keyId) {
        showToast('Failed to create Razorpay order', 'error');
        return;
      }

      const options = {
        key: payload.keyId,
        amount: Math.round(Number(payload.totalAmount || 0) * 100),
        currency: payload.currency || 'INR',
        name: 'LCS',
        description: `Wallet Recharge (${payload.invoiceNumber || 'LCS'})`,
        order_id: payload.orderId,
        prefill: {
          name: wallet?.organization?.organizationName || '',
          email: wallet?.organization?.email || '',
          contact: wallet?.organization?.phoneNumber || '',
        },
        notes: {
          topupRequestId: payload.topupRequestId,
          invoiceNumber: payload.invoiceNumber,
        },
        handler: async (response) => {
          try {
            await OrgUserService.confirmWalletTopup({
              topupRequestId: payload.topupRequestId,
              razorpayOrderId: response.razorpay_order_id,
              razorpayPaymentId: response.razorpay_payment_id,
              razorpaySignature: response.razorpay_signature,
            });

            showToast('Wallet top-up successful');
            closeTopupModal();
            loadWallet();
            loadTransactions(1, transactionFilter === 'all' ? '' : transactionFilter, startDate, endDate);
          } catch (confirmErr) {
            // eslint-disable-next-line no-console
            console.error('Failed to confirm Razorpay payment', confirmErr);
            showToast(confirmErr.message || 'Payment captured but confirmation failed', 'error');
          }
        },
      };

      const razorpay = new globalThis.Razorpay(options);
      razorpay.open();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Topup failed', err);
      showToast(err.message || 'Failed to initiate top-up', 'error');
    }
  };

  const handleDownloadInvoice = async (transactionId) => {
    try {
      const blob = await OrgUserService.downloadWalletTopupInvoice(transactionId);
      const url = globalThis.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `wallet-invoice-${transactionId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      globalThis.URL.revokeObjectURL(url);
      showToast('Invoice downloaded successfully');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to download invoice', err);
      showToast(err.message || 'Failed to download invoice', 'error');
    }
  };

  const totalTopups = transactions
    .filter((txn) => String(txn.type || '').toLowerCase() === 'credit' && String(txn.status || '').toLowerCase() === 'completed')
    .reduce((sum, txn) => sum + Number(txn.amount || 0), 0);

  const totalSpent = transactions
    .filter((txn) => String(txn.type || '').toLowerCase() === 'debit' && String(txn.status || '').toLowerCase() === 'completed')
    .reduce((sum, txn) => sum + Number(txn.amount || 0), 0);

  return (
    <div className="wallet-page">
      {toast.show && (
        <div className={`wallet-toast wallet-toast-${toast.type}`}>
          {toast.message}
        </div>
      )}
      <div className="page-header">
        <h1>Wallet Management</h1>
        <p>Manage your wallet balance and transactions</p>
      </div>

      <div className="wallet-overview">
        <div className="wallet-balance-card">
          <div className="balance-label">Available Balance</div>
          <div className="balance-amount">₹{wallet ? Number(wallet.balance || 0).toLocaleString() : '0'}</div>
          <div className="balance-info">Current wallet balance</div>
        </div>
        <div className="wallet-stats-card">
          <div className="stats-card-title">Quick Stats</div>
          <div className="stats-grid">
            <div className="stat-item">
              <span className="stat-icon">₹</span>
              <span className="stat-label">Total Top-ups</span>
              <span className="stat-value">₹{Number(totalTopups).toLocaleString()}</span>
            </div>
            <div className="stat-item">
              <span className="stat-icon">📊</span>
              <span className="stat-label">Total Spent</span>
              <span className="stat-value">₹{Number(totalSpent).toLocaleString()}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="wallet-actions">
        <button className="action-button primary" onClick={openTopupModal}>💳 Add Money</button>
        <button className="action-button secondary" onClick={handleDownloadStatement} disabled={downloading}>
          📜 {downloading ? 'Downloading...' : 'Download Statement'}
        </button>
      </div>

      <div className="transactions-section">
        <div className="transactions-header">
          <h3>Transaction History</h3>
          <div className="filter-tabs">
            <button className={`filter-tab ${transactionFilter === 'all' ? 'active' : ''}`} onClick={() => handleFilterChange('all')}>All</button>
            <button className={`filter-tab ${transactionFilter === 'credit' ? 'active' : ''}`} onClick={() => handleFilterChange('credit')}>Credit</button>
            <button className={`filter-tab ${transactionFilter === 'debit' ? 'active' : ''}`} onClick={() => handleFilterChange('debit')}>Debit</button>
          </div>
        </div>
        <div className="date-filter-section">
          <div className="date-filter-inputs">
            <div className="date-input-group">
              <label htmlFor="wallet-from-date">From Date:</label>
              <input 
                id="wallet-from-date"
                type="date" 
                value={startDate} 
                onChange={(e) => setStartDate(e.target.value)}
                className="date-input"
              />
            </div>
            <div className="date-input-group">
              <label htmlFor="wallet-to-date">To Date:</label>
              <input 
                id="wallet-to-date"
                type="date" 
                value={endDate} 
                onChange={(e) => setEndDate(e.target.value)}
                className="date-input"
              />
            </div>
            <button className="btn-filter" onClick={handleDateFilter}>Apply Filter</button>
            <button className="btn-filter-clear" onClick={clearDateFilter}>Clear</button>
          </div>
        </div>

        <div className="table-container">
          {loading ? (
            <div className="wallet-loading-message">Loading transactions...</div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Transaction ID</th>
                  <th>Type</th>
                  <th>Description</th>
                  <th>Performed By</th>
                  <th>Amount</th>
                  <th>Balance</th>
                  <th>Date and Time</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {transactions.length === 0 ? (
                  <tr>
                    <td colSpan="8" style={{ textAlign: 'center', padding: '2rem' }}>
                      No transactions found
                    </td>
                  </tr>
                ) : transactions.map((trans) => (
                  <tr key={trans.id}>
                    <td>{trans.id}</td>
                    <td><span className={`type-badge ${trans.type}`}>{trans.type}</span></td>
                    <td>{stripPerformedBy(trans.description)}</td>
                    <td>{extractPerformedBy(trans)}</td>
                    <td>
                      <span className={`amount-value ${trans.type}`}>
                        {trans.type === 'credit' ? '+' : '-'}{Number(trans.amount || 0).toLocaleString()}
                      </span>
                    </td>
                    <td>
                      {trans.balanceAfter != null
                        ? <span style={{ fontWeight: 500 }}>₹{Number(trans.balanceAfter).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        : <span style={{ color: '#aaa' }}>-</span>}
                    </td>
                    <td>{new Date(trans.createdAt).toLocaleString('en-IN', { 
                      dateStyle: 'medium', 
                      timeStyle: 'short' 
                    })}</td>
                    <td>
                      <button className="btn-icon-view" onClick={() => setSelectedTransaction(trans)} title="View Details">
                        <FaEye />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {!loading && transactions.length > 0 && (
            <div className="pagination-section">
              <div className="pagination-info">
                Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} transactions
              </div>
              <div className="pagination-controls">
                <button 
                  className="pagination-btn" 
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                >
                  Previous
                </button>
                <span className="pagination-pages">
                  Page {pagination.page} of {pagination.pages}
                </span>
                <button 
                  className="pagination-btn" 
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page >= pagination.pages}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {typeof document !== 'undefined' && createPortal(
        <>
          {selectedTransaction && (
            <div
              className="modal-overlay"
              onMouseDown={(e) => {
                if (e.target === e.currentTarget) {
                  setSelectedTransaction(null);
                }
              }}
            >
              <div className="modal-content">
                <div className="modal-header">
                  <h2>Transaction Details</h2>
                  <button className="btn-close" onClick={() => setSelectedTransaction(null)} aria-label="Close details">×</button>
                </div>
                <div className="modal-body topup-modal-body">
                  <div className="topup-summary-card transaction-summary-card">
                    <div className="topup-summary-title">Transaction Summary</div>
                    <div className="topup-summary-row">
                      <span>Transaction ID</span>
                      <strong>{selectedTransaction.id}</strong>
                    </div>
                    <div className="topup-summary-row">
                      <span>Type</span>
                      <span className={`type-badge ${selectedTransaction.type}`}>{selectedTransaction.type}</span>
                    </div>
                    <div className="topup-summary-row">
                      <span>Description</span>
                      <strong>{stripPerformedBy(selectedTransaction.description)}</strong>
                    </div>
                    <div className="topup-summary-row">
                      <span>Performed By</span>
                      <strong>{extractPerformedBy(selectedTransaction)}</strong>
                    </div>
                    <div className="topup-summary-row">
                      <span>Date</span>
                      <strong>{new Date(selectedTransaction.createdAt).toLocaleString()}</strong>
                    </div>
                    {selectedTransaction.type === 'credit' && selectedTransaction.invoiceNumber && (
                      <div className="topup-summary-row">
                        <span>Invoice No</span>
                        <strong>{selectedTransaction.invoiceNumber}</strong>
                      </div>
                    )}
                    <div className="topup-summary-row topup-total-row">
                      <span>Amount</span>
                      <strong className={`amount-value-large ${selectedTransaction.type}`}>
                        {selectedTransaction.type === 'credit' ? '+' : '-'}{formatAmount(selectedTransaction.amount)}
                      </strong>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  {selectedTransaction.type === 'credit' && selectedTransaction.invoiceNumber && (
                    <button
                      type="button"
                      className="btn-primary"
                      onClick={() => handleDownloadInvoice(selectedTransaction.id)}
                    >
                      Download Invoice
                    </button>
                  )}
                  <button className="btn-secondary" onClick={() => setSelectedTransaction(null)}>Close</button>
                </div>
              </div>
            </div>
          )}

          {topupModalOpen && (
            <div
              className="modal-overlay"
              onMouseDown={(e) => {
                if (e.target === e.currentTarget) {
                  closeTopupModal();
                }
              }}
            >
              <div className="modal-content">
                <div className="modal-header topup-modal-header">
                  <button className="btn-close" onClick={closeTopupModal} aria-label="Close recharge">×</button>
                </div>
                <form onSubmit={handleInitiateTopup}>
                  <div className="modal-body topup-modal-body">
                    <div className="topup-input-section">
                      <label className="detail-label" htmlFor="topup-taxable-amount">Taxable Amount (₹)</label>
                      <input
                        id="topup-taxable-amount"
                        type="number"
                        min={MIN_TOPUP_AMOUNT}
                        step="0.01"
                        value={topupAmount}
                        onChange={(e) => {
                          setTopupAmount(e.target.value);
                          recalculateTopupPreview(e.target.value);
                        }}
                        className="topup-input"
                        placeholder={`Minimum ₹${MIN_TOPUP_AMOUNT}`}
                      />
                      <div className="topup-quick-options">
                        {QUICK_TOPUP_AMOUNTS.map((amount) => (
                          <button
                            key={amount}
                            type="button"
                            className={`topup-quick-option ${Number.parseFloat(topupAmount || 0) === amount ? 'active' : ''}`}
                            onClick={() => applyTopupAmount(amount)}
                          >
                            ₹{amount.toLocaleString('en-IN')}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="topup-summary-card">
                      <div className="topup-summary-title">Recharge Summary</div>
                      <div className="topup-summary-row">
                        <span>Taxable Amount</span>
                        <strong>₹{formatAmount(topupPreview.taxableAmount)}</strong>
                      </div>
                      <div className="topup-summary-row">
                        <span>Platform Fee (2%)</span>
                        <strong>₹{formatAmount(topupPreview.platformFee)}</strong>
                      </div>
                      <div className="topup-summary-row">
                        <span>GST (18%)</span>
                        <strong>₹{formatAmount(topupPreview.gstAmount)}</strong>
                      </div>
                      {!topupPreview.isInterState && (
                        <>
                          <div className="topup-summary-row">
                            <span>CGST (9%)</span>
                            <strong>₹{formatAmount(topupPreview.cgstAmount)}</strong>
                          </div>
                          <div className="topup-summary-row">
                            <span>SGST (9%)</span>
                            <strong>₹{formatAmount(topupPreview.sgstAmount)}</strong>
                          </div>
                        </>
                      )}
                      {topupPreview.isInterState && (
                        <div className="topup-summary-row">
                          <span>IGST (18%)</span>
                          <strong>₹{formatAmount(topupPreview.igstAmount)}</strong>
                        </div>
                      )}
                      <div className="topup-summary-row topup-total-row">
                        <span>Total Payable</span>
                        <strong>₹{formatAmount(topupPreview.totalAmount)}</strong>
                      </div>
                    </div>

                    <p className="wallet-topup-info-text">
                      A 2% platform fee and 18% GST are auto-calculated on the recharge amount. IGST is applied for inter-state billing, otherwise CGST and SGST are split equally.
                    </p>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn-secondary" onClick={closeTopupModal}>Cancel</button>
                    <button type="submit" className="btn-primary">Proceed to Razorpay</button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </>,
        document.body,
      )}
    </div>
  );
}
